"use client";

import type { Map as LeafletMap } from "leaflet";
import { useEffect, useMemo, useRef, useState } from "react";
import { createClientLogger } from "@/lib/logging/client";
import { cn } from "@/lib/utils";

const logger = createClientLogger("components/movement/static-route-map-card");

const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

type LeafletContainerElement = HTMLDivElement & {
  _leaflet_id?: number;
};

function resetLeafletContainer(element: HTMLDivElement | null) {
  if (!element) return;
  const leafletElement = element as LeafletContainerElement;
  if (leafletElement._leaflet_id !== undefined) {
    delete leafletElement._leaflet_id;
  }
  element.innerHTML = "";
}

type StaticRouteMapMode = "tile" | "styled";

type StaticRouteMapCardProps = {
  mapId: string;
  coordinates: [number, number][];
  segments?: [number, number][][];
  mode?: StaticRouteMapMode;
  showStartEnd?: boolean;
  showAttribution?: boolean;
  emptyStateLabel?: string;
  className?: string;
};

function getBoundsForCoordinates(
  coordinates: [number, number][],
): [[number, number], [number, number]] | null {
  if (!coordinates || coordinates.length === 0) return null;

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  for (const [lat, lng] of coordinates) {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  }

  if (
    !Number.isFinite(minLat) ||
    !Number.isFinite(maxLat) ||
    !Number.isFinite(minLng) ||
    !Number.isFinite(maxLng)
  ) {
    return null;
  }

  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
}

function getProjectedRoutePath(
  coordinates: [number, number][],
  segments?: [number, number][][],
) {
  if (coordinates.length === 0)
    return { d: "", points: [] as [number, number][] };

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  for (const [lat, lng] of coordinates) {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  }

  const lngSpan = Math.max(1e-6, maxLng - minLng);
  const latSpan = Math.max(1e-6, maxLat - minLat);
  const scale = Math.min(82 / lngSpan, 82 / latSpan);
  const drawnWidth = lngSpan * scale;
  const drawnHeight = latSpan * scale;
  const xOffset = 9 + (82 - drawnWidth) / 2;
  const yOffset = 9 + (82 - drawnHeight) / 2;

  const points: [number, number][] = coordinates.map(([lat, lng]) => {
    const x = xOffset + (lng - minLng) * scale;
    const y = yOffset + (maxLat - lat) * scale;
    return [x, y];
  });

  const starts = new Set<number>([0]);
  let offset = 0;
  for (const segment of segments ?? []) {
    starts.add(offset);
    offset += segment.length;
  }
  const d = points
    .map(
      (point, index) =>
        `${starts.has(index) ? "M" : "L"} ${point[0].toFixed(2)} ${point[1].toFixed(2)}`,
    )
    .join(" ");

  return { d, points };
}

function StyledRouteMap({
  coordinates,
  segments,
  showStartEnd,
  emptyStateLabel,
}: {
  coordinates: [number, number][];
  segments?: [number, number][][];
  showStartEnd: boolean;
  emptyStateLabel: string;
}) {
  if (coordinates.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-[11px] text-slate-300">
        {emptyStateLabel}
      </div>
    );
  }

  const projected = getProjectedRoutePath(coordinates, segments);
  const start = projected.points[0];
  const end = projected.points[projected.points.length - 1];

  return (
    <div className="h-full w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <svg
        viewBox="0 0 100 100"
        className="h-full w-full"
        role="img"
        aria-label="Route preview map"
      >
        <title>Route preview map</title>
        <defs>
          <pattern
            id="grid-pattern"
            width="10"
            height="10"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 10 0 L 0 0 0 10"
              fill="none"
              stroke="rgba(148,163,184,0.14)"
              strokeWidth="0.35"
            />
          </pattern>
        </defs>
        <rect x="0" y="0" width="100" height="100" fill="url(#grid-pattern)" />

        {projected.points.length > 1 ? (
          <>
            <path
              d={projected.d}
              fill="none"
              stroke="rgba(255,59,48,0.3)"
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d={projected.d}
              fill="none"
              stroke="#FF3B30"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        ) : null}

        {showStartEnd && start ? (
          <circle
            cx={start[0]}
            cy={start[1]}
            r="2.4"
            fill="#22c55e"
            stroke="white"
            strokeWidth="0.9"
          />
        ) : null}
        {showStartEnd && end && projected.points.length > 1 ? (
          <circle
            cx={end[0]}
            cy={end[1]}
            r="2.4"
            fill="#ef4444"
            stroke="white"
            strokeWidth="0.9"
          />
        ) : null}
      </svg>
    </div>
  );
}

export function StaticRouteMapCard({
  mapId,
  coordinates = [],
  segments,
  mode = "tile",
  showStartEnd = false,
  showAttribution = false,
  emptyStateLabel = "No route points",
  className,
}: StaticRouteMapCardProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapHostRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isTileMapReady, setIsTileMapReady] = useState(false);
  const [tileMapFailed, setTileMapFailed] = useState(false);

  useEffect(() => {
    if (mode !== "tile") return;
    if (isVisible) return;
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isVisible, mode]);

  const bounds = useMemo(
    () => getBoundsForCoordinates(coordinates),
    [coordinates],
  );
  const center = coordinates[0] ?? ([0, 0] as [number, number]);

  const hasAnyPoints = coordinates.length > 0;
  const hasRoute = coordinates.length > 1;
  const shouldAttemptTileMap =
    mode === "tile" && isVisible && hasAnyPoints && !tileMapFailed;
  const shouldRenderStyledMap = mode === "styled" || tileMapFailed;

  useEffect(() => {
    if (mode !== "tile") return;
    if (!shouldAttemptTileMap) {
      setIsTileMapReady(false);
      return;
    }

    const hostElement = mapHostRef.current;
    if (!hostElement) return;

    let isDisposed = false;
    let map: LeafletMap | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const initializeMap = async () => {
      try {
        const L = await import("leaflet");
        if (isDisposed) return;

        resetLeafletContainer(hostElement);

        map = L.map(hostElement, {
          center: center,
          zoom: 14,
          zoomControl: false,
          attributionControl: false,
          dragging: false,
          scrollWheelZoom: false,
          doubleClickZoom: false,
          boxZoom: false,
          touchZoom: false,
          keyboard: false,
          preferCanvas: true,
          fadeAnimation: false,
          zoomAnimation: false,
          markerZoomAnimation: false,
        });

        // Use Leaflet's fitBounds for accurate zoom based on container size
        if (bounds) {
          map.fitBounds(bounds, {
            animate: false,
            padding: [10, 10],
            maxZoom: 17,
          });
        }

        const tileLayer = L.tileLayer(TILE_URL, {
          maxZoom: 19,
        }).addTo(map);

        // Track tile loading to show map only when tiles are ready
        let tilesLoaded = false;
        const onTilesLoaded = () => {
          if (isDisposed || tilesLoaded) return;
          tilesLoaded = true;
          setIsTileMapReady(true);
          setTileMapFailed(false);
        };

        tileLayer.on("load", onTilesLoaded);

        // Fallback timeout in case tiles take too long or load event doesn't fire
        setTimeout(() => {
          if (!isDisposed && !tilesLoaded) {
            onTilesLoaded();
          }
        }, 2000);

        if (hasRoute) {
          L.polyline(segments ?? coordinates, {
            color: "#FF3B30",
            weight: 4,
            opacity: 1,
            lineCap: "round",
            lineJoin: "round",
          }).addTo(map);
        }

        if (showStartEnd && hasAnyPoints) {
          L.circleMarker(coordinates[0], {
            radius: 6,
            color: "#ffffff",
            fillColor: "#22c55e",
            fillOpacity: 1,
            weight: 2,
          }).addTo(map);
        }

        if (showStartEnd && hasRoute) {
          L.circleMarker(coordinates[coordinates.length - 1], {
            radius: 6,
            color: "#ffffff",
            fillColor: "#ef4444",
            fillOpacity: 1,
            weight: 2,
          }).addTo(map);
        }

        map.invalidateSize(false);

        // Cards change size after first paint (the journal's responsive grid,
        // a window resize, rotating a tablet). Leaflet only measured once, so
        // the tiles and route stayed drawn at the old width.
        if (typeof ResizeObserver !== "undefined" && hostElement) {
          let frame = 0;
          resizeObserver = new ResizeObserver(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
              if (!map || isDisposed) return;
              map.invalidateSize(false);
              if (bounds) {
                map.fitBounds(bounds, {
                  animate: false,
                  padding: [10, 10],
                  maxZoom: 17,
                });
              }
            });
          });
          resizeObserver.observe(hostElement);
        }
      } catch (error) {
        if (isDisposed) return;
        setIsTileMapReady(false);
        setTileMapFailed(true);
        logger.error("Failed to initialize static route map", error);
      }
    };

    setIsTileMapReady(false);
    setTileMapFailed(false);
    void initializeMap();

    return () => {
      isDisposed = true;
      resizeObserver?.disconnect();
      if (map) {
        try {
          map.off();
          map.remove();
        } catch (error) {
          logger.warn("Failed to dispose static route map", error);
        }
      }
      resetLeafletContainer(hostElement);
    };
  }, [
    bounds,
    center,
    coordinates,
    segments,
    hasAnyPoints,
    hasRoute,
    mode,
    shouldAttemptTileMap,
    showStartEnd,
  ]);

  return (
    <div
      ref={containerRef}
      className={cn("relative h-full w-full overflow-hidden", className)}
    >
      {shouldRenderStyledMap ? (
        <StyledRouteMap
          coordinates={coordinates}
          segments={segments}
          showStartEnd={showStartEnd}
          emptyStateLabel={emptyStateLabel}
        />
      ) : shouldAttemptTileMap ? (
        <>
          <div
            key={`static-route-${mapId}`}
            ref={mapHostRef}
            style={{ height: "100%", width: "100%" }}
            className="z-0"
          />

          {!isTileMapReady ? (
            <div className="pointer-events-none absolute inset-0 overflow-hidden bg-muted">
              <div className="absolute inset-0 -translate-x-full animate-shimmer-fast bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            </div>
          ) : null}

          {showAttribution ? (
            <div className="pointer-events-none absolute right-1 bottom-1 rounded bg-black/55 px-1.5 py-0.5 text-[9px] text-white">
              © OSM
            </div>
          ) : null}
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-muted text-[11px] text-text-secondary">
          {hasAnyPoints ? "Loading map..." : emptyStateLabel}
        </div>
      )}
    </div>
  );
}
